import { NgZone } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { CameraFollowController } from './camera-follow-controller';

/**
 * Opciones para centrar la cámara en un punto
 */
export interface CenterOnPointOptions {
  coordinates: [number, number];
  zoom?: number; // Si no se especifica, mantiene el zoom actual
  animated?: boolean; // true = flyTo, false = jumpTo
  duration?: number; // Duración de animación en ms (default: 1000)
  preserveZoom?: boolean; // Si true, ignora el parámetro zoom y mantiene el actual
}

/**
 * Servicio centralizado para control de cámara en mapas Mapbox
 * Responsabilidades:
 * - Seguimiento suave de vehículos (via CameraFollowController)
 * - Centrado en puntos específicos (búsquedas, paradas, etc.)
 * - Gestión de estado de seguimiento
 * - API unificada para todo el componente
 */
export class MapCameraService {
  private cameraFollowController?: CameraFollowController;
  private isFollowing: boolean = false;
  private currentTargetProvider?: () => { lng: number; lat: number };
  private centerOnPointTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private map: mapboxgl.Map,
    private ngZone: NgZone,
  ) {}

  /**
   * Inicia el seguimiento suave de un objetivo móvil (vehículo)
   * @param getTargetPosition Función que devuelve la posición actual del objetivo
   * @param onPositionUpdate Callback opcional para notificar cambios de posición
   */
  followTarget(
    getTargetPosition: () => { lng: number; lat: number },
    onPositionUpdate?: () => void,
  ): void {
    // Si ya hay un seguimiento activo, detenerlo primero
    if (this.cameraFollowController) {
      this.stopFollow();
    }

    // Guardar provider para posible reanudación
    this.currentTargetProvider = getTargetPosition;

    // Crear nuevo controlador de seguimiento
    this.cameraFollowController = new CameraFollowController(
      this.map,
      getTargetPosition,
      this.ngZone,
      onPositionUpdate,
    );

    // Iniciar seguimiento
    this.cameraFollowController.start();
    this.isFollowing = true;
  }

  /**
   * Detiene cualquier seguimiento activo
   */
  stopFollow(): void {
    if (this.cameraFollowController) {
      this.cameraFollowController.destroy();
      this.cameraFollowController = undefined;
      this.isFollowing = false;
      this.currentTargetProvider = undefined;
    }
  }

  /**
   * Verifica si hay un seguimiento activo
   */
  isFollowingActive(): boolean {
    return this.isFollowing;
  }

  /**
   * Centra la cámara en un punto específico
   * Útil para búsquedas, paradas, recorridos, etc.
   *
   * @param options Opciones de centrado
   */
  centerOnPoint(options: CenterOnPointOptions): void {
    const {
      coordinates,
      zoom,
      animated = true,
      duration = 1000,
      preserveZoom = false,
    } = options;

    // Validar coordenadas
    if (!coordinates || coordinates.length !== 2) {
      console.warn('[MapCameraService] Coordenadas inválidas:', coordinates);
      return;
    }

    const [lng, lat] = coordinates;
    if (
      typeof lng !== 'number' ||
      typeof lat !== 'number' ||
      isNaN(lng) ||
      isNaN(lat)
    ) {
      console.warn('[MapCameraService] Coordenadas no numéricas:', {
        lng,
        lat,
      });
      return;
    }

    // Forzar resize antes de centrar (por si cambió el tamaño del contenedor)
    this.map.resize();

    // Determinar zoom a usar
    const targetZoom = preserveZoom
      ? this.map.getZoom()
      : (zoom ?? this.map.getZoom());

    if (animated) {
      // Centrado animado con flyTo
      clearTimeout(this.centerOnPointTimeout);
      this.centerOnPointTimeout = setTimeout(() => {
        this.map.flyTo({
          center: [lng, lat],
          zoom: targetZoom,
          duration,
        });
      }, 100); // Pequeño delay para que el resize se complete
    } else {
      // Centrado instantáneo con jumpTo
      this.map.jumpTo({
        center: [lng, lat],
        zoom: targetZoom,
      });
    }
  }

  /**
   * Centra la cámara en un punto SIN cambiar el zoom
   * Útil para seguir vehículos no seleccionados sin molestar al usuario
   *
   * @param coordinates Coordenadas [lng, lat]
   * @param duration Duración de animación (default: 800ms)
   */
  centerPreservingZoom(
    coordinates: [number, number],
    duration: number = 800,
  ): void {
    this.centerOnPoint({
      coordinates,
      preserveZoom: true,
      animated: true,
      duration,
    });
  }

  /**
   * Destruye el servicio y limpia recursos
   */
  destroy(): void {
    this.stopFollow();
    clearTimeout(this.centerOnPointTimeout);
  }
}
